import { z } from "zod"
import DOMPurify from "dompurify"

const fqdnUrlRegex = /^https?:\/\/([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/[^\s]*)?$/

export const GuidanceSchema = z.object({
  action: z.string().min(1, "Action is required"),
  organisation: z.string().min(1, "Organisation is required"),
})

export type GuidanceSchema = z.infer<typeof GuidanceSchema>

export const GuidanceFormSchema = z.object({
  organisation: z.string().min(1, "Organisation is required"),
  name: z
    .string()
    .trim()
    .min(1, "Name is required")
    .max(100, "Name must be 100 characters or fewer")
    .regex(/^[a-zA-Z0-9\s]+$/, "'Name' can only be alpha numeric and spaces"),

  code: z
    .string()
    .trim()
    .min(1, "Code is required")
    .max(50, "Code must be 50 characters or fewer")
    .regex(/^[a-zA-Z0-9\s]+$/, "'Code' can only be alpha numeric and spaces"),
  value: z
    .string()
    .min(1, "Value is required")
    .refine(
      (val) => {
        const sanitized = DOMPurify.sanitize(val)
        const textContent = new DOMParser().parseFromString(sanitized, "text/html").body.textContent?.trim() ?? ""
        return textContent.length > 0
      },
      { message: "Value must contain actual content, not just formatting" },
    )
    .transform((val) => {
      const sanitized = DOMPurify.sanitize(val)
      return sanitized
        .replaceAll(/&nbsp;|\u00A0/g, " ")
        .replaceAll(/(\s)+/g, "$1")
        .trim()
    }),
})

export type GuidanceFormData = z.infer<typeof GuidanceFormSchema>

export const LinkInputSchema = z
  .object({
    protocol: z.enum(["https://", "http://"]),
    urlPath: z
      .string()
      .min(1, "URL is required")
      .refine((path) => !path.includes("://"), {
        message: "Don't include http:// or https:// - select it from the dropdown",
      })
      .refine((path) => !path.startsWith("#") && !path.startsWith("/"), {
        message: "Please enter a valid domain (e.g., example.com or example.com/path)",
      })
      .refine((path) => /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+(\/[^\s]*)?$/.test(path), {
        message: "Please enter a valid domain (e.g., example.com or example.com/path)",
      }),
  })
  .transform((data) => data.protocol + data.urlPath)
  .pipe(
    z
      .string()
      .url("Please enter a valid fully qualified URL")
      .refine((url) => fqdnUrlRegex.test(url), {
        message: "Please enter a valid fully qualified URL",
      }),
  )

export type LinkInputData = z.input<typeof LinkInputSchema>

export const validateFqdnUrl = (url: string): boolean => {
  if (!url || url.trim() === "") return false

  if (url.startsWith("#") || url.startsWith("/") || url.startsWith("./") || url.startsWith("../")) {
    return false
  }

  return fqdnUrlRegex.test(url)
}

export const splitUrl = (url: string): { protocol: "https://" | "http://"; path: string } => {
  const regex = /^(https?:\/\/)(.+)$/
  const match = regex.exec(url)

  if (match != null) {
    const protocol = match[1]
    const path = match[2]

    if (protocol === "https://" || protocol === "http://") {
      return { protocol, path }
    }
  }

  return { protocol: "https://", path: url }
}
